import { Badge, Skeleton } from '@fruuit/ui'
import { FlaskConical, Rocket, ShieldAlert, ShieldCheck } from 'lucide-react'
import { useAdminSystem } from './api'
import { ModeCallout } from './ModeCallout'

/**
 * Top of the handbook: which kind of deployment this is (live from /admin/system) and a key
 * to the two callout styles used throughout.
 */
export function DeploymentSummary() {
  const system = useAdminSystem()
  const data = system.data
  return (
    <section
      aria-label="This deployment"
      className="grid gap-5 rounded-lg border border-line bg-wash p-5 sm:p-6 lg:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)] lg:gap-8"
    >
      <div className="min-w-0">
        <p className="text-xs font-semibold uppercase tracking-[var(--tracking-kicker)] text-teal-500">
          This deployment
        </p>
        <div className="mt-2.5 flex flex-wrap items-center gap-2" data-testid="docs-mode">
          {data ? (
            <>
              {data.mode === 'demo' ? (
                <Badge tone="watch" icon={<FlaskConical />} className="text-sm">
                  Demo deployment
                </Badge>
              ) : (
                <Badge tone="brand" icon={<Rocket />} className="text-sm">
                  Production deployment
                </Badge>
              )}
              <Badge tone="outline">Version {data.version}</Badge>
              <Badge
                tone={data.auth.privileged_mfa === 'required' ? 'positive' : 'watch'}
                icon={data.auth.privileged_mfa === 'required' ? <ShieldCheck /> : <ShieldAlert />}
              >
                MFA {data.auth.privileged_mfa}
              </Badge>
              <Badge tone="outline">{data.feeds.length} demo feeds</Badge>
            </>
          ) : system.isError ? (
            <Badge tone="quiet">Live status unavailable</Badge>
          ) : (
            <Skeleton className="h-6 w-64" />
          )}
        </div>
        {data ? (
          <ul className="mt-3 flex flex-col gap-1 text-sm leading-relaxed text-ink-soft">
            {data.mode_reasons.map((reason) => (
              <li key={reason} className="flex gap-2">
                <span aria-hidden="true" className="mt-2 size-1 shrink-0 rounded-full bg-sage" />
                {reason}
              </li>
            ))}
          </ul>
        ) : null}
      </div>
      <div className="flex min-w-0 flex-col gap-3">
        <ModeCallout mode="demo" className="py-2.5 text-sm">
          <p>What is simplified or different in this demo deployment.</p>
        </ModeCallout>
        <ModeCallout mode="production" className="py-2.5 text-sm">
          <p>
            What the production build changes (PRD and agreement), and what must be done before real
            client data.
          </p>
        </ModeCallout>
      </div>
    </section>
  )
}
